import { batchActions } from 'redux-batched-actions'
import { all, call, put, take } from 'redux-saga/effects'
import { calendarsFetched } from '../../actions/bookingCalendar/calendar/fetchCalendar'
import { callFetch } from '../../actions/fetch'
import { licenseAccepted } from '../../actions/userAccount/user/fetchLicense'
import {
  saveUser,
  userRejected,
} from '../../actions/userAccount/user/fetchUser'
import { switchLanguage } from '../../actions/userAccount/user/switchLanguage'
import { countryDictionaryAccepted } from '../../actions/userObject/settings/countryDictionary'
import { changeDateTimeFormat } from '../../actions/userObject/settings/dateTimeFormat'
import { objectTypeAccepted } from '../../actions/userObject/settings/objectTypes'
import { dataForUserSettingsAccepted } from '../../actions/userObject/settings/requestFetchUserSettings'
import { ILicense, LicenseType } from '../../models/License'
import { ActionNames } from '../../utils/FetchActionsName'
import fetchActionsName from '../../utils/FetchActionsName/fetchActionsName'
import { Mixpanel } from '../../utils/Mixpanel'
import { FacilitiesActions } from '../../reducers/facilities/actionNames'
import { checkDate } from '../../reducers/userAccount/selectors/license'
import loadEverything from '../../models/User/loadEverything'
import fetchCookiesInfo from '../../models/User/fetchCookiesInfo'
import { mapLicencesResponse } from '../../models/License/request/handleResponse'
import { acceptTemplates } from '../../actions/templates/loadTemplates'
import { groupBy } from 'lodash'
import {
  NotificationActions,
  UserAccountActions,
} from '../../reducers/userAccount/actionNames'
import { CalendarActions } from '../../reducers/bookingCalendar/actionNames'
import { PriceConfigurationActions } from '../../reducers/price/actionNames'
import { mapResponseNotification } from '../../models/Notifications/request/handleResponse'
import mapResponseDatesToColumns from '../../models/Calendar/mappers/mapResponseDatesToColumns'
import mapResponseToPriceConfiguration from '../../models/Price/mappers/mapResponseToPriceConfiguration'
import mapResponseToSettings from '../../models/Object/mappers/mapResponseToSettings'
import { getSubs } from './loadLicense'
import {
  readCookiesRaw,
  storeCookies,
  storeCookiesExpire,
} from '../../containers/Cookies/storage'
import { localStorage as LocalStorage } from '../../database'
import {
  hasApiReservationFormSettings,
  mapApiToFieldVisibility,
  readLocalFieldVisibility,
} from '../../models/User/mappers/mapReservationFormSettings'

function* mergeCookies(user) {
  const { cookies_information } = user
  const cookies = readCookiesRaw()
  if (!cookies_information) {
    if (!cookies) return

    yield put({ type: UserAccountActions.SaveCookies })
  } else {
    storeCookies(cookies_information)
  }
}

function* mergeReservationFormSettings(user) {
  if (hasApiReservationFormSettings(user.reservation_form_settings)) {
    const { UserDriver: Driver, Names } = LocalStorage
    const { ReservationForm } = Names
    const fieldVisibility = mapApiToFieldVisibility(
      user.reservation_form_settings,
    )

    Driver.setItem(
      ReservationForm.FieldVisibility,
      JSON.stringify(fieldVisibility),
    )
    return
  }

  if (readLocalFieldVisibility()) {
    yield put({ type: UserAccountActions.SaveReservationFormSettings })
  }
}

function* load() {
  // yield Effects.put(setDataLoading(true))

  try {
    const allNames = fetchActionsName(ActionNames.LoadEverything)
    const [allService] = yield all([
      take(allNames.output),
      put(callFetch(allNames.input, allNames.output, loadEverything)),
    ])

    const allData = yield call(allService.service)
    const subs = yield* getSubs()

    const user = allData.user
    const countries = allData.countries
    const objectTypes = allData.object_types
    const license = mapLicencesResponse(
      { items: [allData.license] },
      { items: allData.license_list },
      subs,
      user,
    )
    const templates = allData.templates
    const calendarId = allData.calendar[0].id_calendar

    const settings = mapResponseToSettings(allData.object)

    yield* mergeCookies(user)
    yield* mergeReservationFormSettings(user)

    // Sync cookies info (including expiry) from API to local storage
    try {
      const fetchNames = fetchActionsName(ActionNames.FetchCookiesInfo)
      const [service] = yield all([
        take(fetchNames.output),
        put(callFetch(fetchNames.input, fetchNames.output, fetchCookiesInfo)),
      ])
      const info = yield call(service.service)
      const cookiesInfo = info && info.items && info.items.cookies_information
      if (cookiesInfo) {
        const {
          necessary,
          functional,
          analytics,
          performance,
          advertisement,
          expire,
        } = cookiesInfo
        const clientCookies = {
          necessary,
          functional,
          analytics,
          performance,
          advertisement,
        }
        if (Object.values(clientCookies).some(v => typeof v === 'boolean')) {
          storeCookies(clientCookies)
        }
        if (expire) {
          const toIso = (s: string) => {
            try {
              const [d, t] = String(s).split(' ')
              if (!d || !t) return undefined
              const parts = t.split(':')
              const hh = (parts[0] || '00').padStart(2, '0')
              const mm = (parts[1] || '00').padStart(2, '0')
              const ss = (parts[2] || '00').padStart(2, '0')
              return `${d}T${hh}:${mm}:${ss}`
            } catch (e) {
              return undefined
            }
          }
          const iso = toIso(expire)
          if (iso) {
            const expDate = new Date(iso)
            if (!isNaN(expDate.getTime())) {
              storeCookiesExpire(expDate.toISOString())
              const now = new Date()
              if (now >= expDate) {
                yield put({
                  type: UserAccountActions.OpenCookies,
                  force: true,
                  step: 1,
                })
              }
            }
          }
        }
      }
    } catch (e) {
      // ignore cookie info errors
    }

    yield put(
      batchActions([
        saveUser(user),
        countryDictionaryAccepted(countries),
        objectTypeAccepted(objectTypes),
        changeDateTimeFormat(user.dateFormat, user.timeFormat),
        dataForUserSettingsAccepted(settings),
        calendarsFetched(Number(calendarId)),
        licenseAccepted(license),
        acceptTemplates(templates, groupBy(templates, 'language')),
        {
          type: NotificationActions.Put,
          notifications: mapResponseNotification(allData.notifications),
        },
        {
          type: FacilitiesActions.FacilitiesAccepted,
          payload: allData.amentity,
        },
        {
          type: CalendarActions.FetchSelectedColumns,
          columns: mapResponseDatesToColumns({
            items: allData.calendar_days,
          } as any),
        },
        {
          type: PriceConfigurationActions.PriceConfigurationAccepted,
          configuration: mapResponseToPriceConfiguration({
            items: [allData.price_settings],
          } as any)[0],
        },
      ]),
    )

    yield put(switchLanguage(user.savedLanguage, false))

    Mixpanel.reset()

    const provider = localStorage.getItem('provider')

    Mixpanel.identify(user.id)

    Mixpanel.register({
      Email:
        user.id_worker && Number(user.id_worker)
          ? user.owner_email
          : user.email,
      ID: user.id,
      Language: user.savedLanguage,
      'Premium Plan Type': isLicenseValid(license),
      'Subaccount ID':
        user.id_worker && Number(user.id_worker) ? user.id_worker : undefined,
      'Subaccount Email':
        user.id_worker && Number(user.id_worker) ? user.email : undefined,
    })

    Mixpanel.track('Login Completed', {
      Email: user.email,
      ID: user.id,
      'Login Method': provider ? provider : 'E-mail',
    })
    Mixpanel.people.union(user.id, 'Platforms Used', 'Web')

    if (user.id_worker && Number(user.id_worker)) {
      localStorage.setItem('workerId', user.id_worker)
    }

    yield put({
      type: FacilitiesActions.LoadObjectFacilities,
      objectId: allData.object.id,
    })
  } catch (e) {
    yield put(userRejected(e.message))
    //yield Effects.put(setDataLoading(false))
  }
}

export const getLicense = (licenses: ILicense[], type: LicenseType) => {
  let license = licenses.find(license => license.licenseType === type)

  if (type === LicenseType.LICENSE_INVOICE) {
    license = {
      ...license,
      link: (
        licenses.find(l => l.licenseType === type && l.link) || {
          link: undefined,
        }
      ).link,
    }
  }

  return license
}

export const isLicenseValid = (licenses: ILicense[]) => {
  const android = getLicense(licenses, LicenseType.LICENSE_WEB_ANDROID)
  const full = getLicense(licenses, LicenseType.LICENSE_WEB_ANDROID_IOS)
  const ios = getLicense(licenses, LicenseType.LICENSE_WEB_IOS)
  const premiumRooms = getLicense(licenses, LicenseType.LICENSE_PREMIUM_ROOMS)
  const premiumBasic = getLicense(licenses, LicenseType.LICENSE_PREMIUM_BASIC)
  const premiumAssistent = getLicense(
    licenses,
    LicenseType.LICENSE_PREMIUM_ASSISTENT,
  )
  const premiumProfessional = getLicense(
    licenses,
    LicenseType.LICENSE_PREMIUM_PROFESSIONAL,
  )

  const lite = getLicense(licenses, LicenseType.LICENSE_PREMIUM_LITE)
  const plus = getLicense(licenses, LicenseType.LICENSE_PREMIUM_PLUS)
  const pro = getLicense(licenses, LicenseType.LICENSE_PREMIUM_PRO)
  const max = getLicense(licenses, LicenseType.LICENSE_PREMIUM_MAX)

  const invoice = getLicense(licenses, LicenseType.LICENSE_INVOICE)
  const booking = getLicense(licenses, LicenseType.LICENSE_BOOKING_COM)
  const icalendar = getLicense(licenses, LicenseType.LICENSE_ICALENDAR)
  const permissions = getLicense(licenses, LicenseType.LICENSE_PERMISSIONS)

  const trial = licenses.filter(l => l.trial === 1)

  const lObj = {
    android: android && checkDate(android.license),
    ios: ios && checkDate(ios.license),
    full: full && checkDate(full.license),
    premiumRooms: premiumRooms && checkDate(premiumRooms.license),
    premiumBasic: premiumBasic && checkDate(premiumBasic.license),
    premiumAssistent: premiumAssistent && checkDate(premiumAssistent.license),
    premiumProfessional:
      premiumProfessional && checkDate(premiumProfessional.license),
    invoice: invoice && checkDate(invoice.license),
    booking: booking && checkDate(booking.license),
    icalendar: icalendar && checkDate(icalendar.license),
    permissions: permissions && checkDate(permissions.license),
    premiumLite: lite && checkDate(lite.license),
    premiumPlus: plus && checkDate(plus.license),
    premiumPro: pro && checkDate(pro.license),
    premiumMax: max && checkDate(max.license),
    trial: trial && trial.some(l => checkDate(l.license)),
  }

  const oldValid = lObj.android || lObj.ios || lObj.full

  let lArr = []
  if (lObj.trial) {
    lArr.push('Trial')
    return lArr
  }
  if (lObj.android) lArr.push('Web + Android')
  if (lObj.ios) lArr.push('Web + iOS')
  if (lObj.full) lArr.push('Web + Android + iOS')
  if (lObj.invoice && oldValid) lArr.push('Faktury')
  if (lObj.booking && oldValid) lArr.push('Booking.com')
  if (lObj.icalendar && oldValid) lArr.push('iCalendar')
  if (lObj.premiumLite) {
    lArr.push('Lite')
    return lArr
  }
  if (lObj.premiumPlus) {
    lArr.push('Plus')
    return lArr
  }
  if (lObj.premiumPro) {
    lArr.push('Pro')
    return lArr
  }
  if (lObj.premiumMax) {
    lArr.push('Max')
    return lArr
  }
  if (lObj.permissions) lArr.push('Uprawnienia')
  if (!lObj.trial && !oldValid) {
    if (
      lObj.premiumAssistent ||
      lObj.premiumProfessional ||
      lObj.premiumBasic ||
      lObj.premiumRooms
    )
      lArr.push('Premium')
    else lArr.push('Free')
  }

  return lArr
}

export default load
